import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./style.css";
import Breadcrumbs from "../../components/breacrumbs";
import BackButton from "../../components/backButton";
import LoadingSpin from "../../components/loading";
import { FormSkeleton } from "../../components/skeleton";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import InputMask from "react-input-mask";
import "react-phone-input-2/lib/style.css";
import ModalPortal from "./ModalPortal";

export default function EditProfileForm() {
  const { user, token } = useAuth();
  const isAdmin = user?.admin === 1;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    nickname: "",
    email: "",
    telephone: "",
    address: "",
    city: "",
    state: "",
    zipcode: "",
    birthday: "",
    graduation: "",
    course: "",
    hometown: "",
    instagram: "",
    facebook: "",
    linkedin: "",
    type: "",
  });
  const [photoGraduation, setPhotoGraduation] = useState(null);
  const [photoNew, setPhotoNew] = useState(null);
  const [previewGraduation, setPreviewGraduation] = useState(null);
  const [previewNew, setPreviewNew] = useState(null);
  const [removePhotoGraduation, setRemovePhotoGraduation] = useState(false);
  const [removePhotoNew, setRemovePhotoNew] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isModalClosing, setIsModalClosing] = useState(false);

  useEffect(() => {
    const userId = searchParams.get("userId") || localStorage.getItem("userId");
    if (userId) {
      setInitialLoading(true);

      // Promise para delay mínimo de loading
      const minLoadingTime = new Promise((resolve) => setTimeout(resolve, 800));

      // Promise para buscar dados
      const fetchData = api.get(`/api/user-profile/${userId}`);

      // Aguarda ambas as promises (dados carregados E tempo mínimo passado)
      Promise.all([fetchData, minLoadingTime])
        .then(([response]) => {
          setProfileData(response.data);
          setInitialLoading(false);
        })
        .catch((err) => {
          console.error("Erro ao buscar perfil:", err);
          // Mesmo com erro, aguarda o tempo mínimo antes de esconder o loading
          setTimeout(() => setInitialLoading(false), 800);
        });
    }
  }, [searchParams]);

  useEffect(() => {
    if (profileData) {
      setFormData({
        name: profileData.name || "",
        nickname: profileData.nickname || "",
        email: profileData.email || "",
        telephone: profileData.telephone || "",
        address: profileData.address || "",
        city: profileData.city || "",
        state: profileData.state || "",
        zipcode: profileData.zipcode || "",
        birthday: profileData.birthday || "",
        graduation: profileData.graduation || "",
        course: profileData.course || "",
        hometown: profileData.hometown || "",
        instagram: profileData.instagram || "",
        facebook: profileData.facebook || "",
        linkedin: profileData.linkedin || "",
        type: profileData.type || "",
      });
      // Carrega as imagens existentes, se houver
      setPreviewGraduation(profileData.photoGraduationFile || null);
      setPreviewNew(profileData.photoNewFile || null);
    }
  }, [profileData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (showDeleteModal) {
      document.body.classList.add("modal-open");
      
      // Função para fechar modal com ESC
      const handleEscKey = (event) => {
        if (event.key === 'Escape') {
          handleCloseDeleteModal();
        }
      };
      
      // Adiciona o listener
      document.addEventListener('keydown', handleEscKey);
      
      // Cleanup function
      return () => {
        document.removeEventListener('keydown', handleEscKey);
      };
    } else {
      document.body.classList.remove("modal-open");
    }
  }, [showDeleteModal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const userId = searchParams.get("userId") || localStorage.getItem("userId");
    setLoading(true); // Exibe o indicador de carregamento
    try {
      // Remove caracteres não numéricos do telefone, mantendo o +
      const sanitizedFormData = {
        ...formData,
        telephone: formData.telephone.replace(/[^\d+]/g, ""),
        removePhotoGraduation,
        removePhotoNew,
      };

      await api.put(`/api/member-update/${userId}`, sanitizedFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      await uploadPhotos(userId);

      // Exibe o toast de sucesso
      toast.success("Informações salvas com sucesso!");

      // Navega para a página inicial
      navigate("/home");
    } catch (error) {
      console.error("Erro ao salvar informações:", error);
      toast.error("Erro ao salvar informações. Tente novamente.");
    } finally {
      setLoading(false); // Oculta o indicador de carregamento
    }
  };

  const uploadPhotos = async (userId) => {
    if (!photoGraduation && !photoNew) return;

    const formData = new FormData();
    formData.append("userId", userId);
    if (photoGraduation) formData.append("photoGraduation", photoGraduation);
    if (photoNew) formData.append("photoNew", photoNew);

    try {
      await api.post("/api/uploadPhoto", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success("Fotos enviadas com sucesso!");
    } catch (err) {
      toast.error("Erro ao enviar fotos.");
      console.error(err);
    }
  };

  // Atualize os handlers dos inputs de arquivo:
  const handlePhotoGraduationChange = (e) => {
    const file = e.target.files[0];
    setPhotoGraduation(file);
    if (file) {
      setPreviewGraduation(URL.createObjectURL(file));
    } else {
      setPreviewGraduation(null);
    }
  };

  const handlePhotoNewChange = (e) => {
    const file = e.target.files[0];
    setPhotoNew(file);
    if (file) {
      setPreviewNew(URL.createObjectURL(file));
    } else {
      setPreviewNew(null);
    }
  };

  const handleOpenDeleteModal = (target) => {
    setDeleteTarget(target); // 'graduation' ou 'new'
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setIsModalClosing(true);
    setTimeout(() => {
      setShowDeleteModal(false);
      setDeleteTarget(null);
      setIsModalClosing(false);
    }, 200); // Tempo da animação de saída
  };

  const handleConfirmDelete = () => {
    if (deleteTarget === "graduation") {
      setPhotoGraduation(null);
      setPreviewGraduation(null);
      setRemovePhotoGraduation(true);
    } else if (deleteTarget === "new") {
      setPhotoNew(null);
      setPreviewNew(null);
      setRemovePhotoNew(true);
    }
    handleCloseDeleteModal();
  };

  const brazilianStates = [
    "Acre",
    "Alagoas",
    "Amapá",
    "Amazonas",
    "Bahia",
    "Ceará",
    "Distrito Federal",
    "Espírito Santo",
    "Goiás",
    "Maranhão",
    "Mato Grosso",
    "Mato Grosso do Sul",
    "Minas Gerais",
    "Pará",
    "Paraíba",
    "Paraná",
    "Pernambuco",
    "Piauí",
    "Rio de Janeiro",
    "Rio Grande do Norte",
    "Rio Grande do Sul",
    "Rondônia",
    "Roraima",
    "Santa Catarina",
    "São Paulo",
    "Sergipe",
    "Tocantins",
  ];

  return (
    <>
      {loading && <LoadingSpin />}

      <div className="container d-flex align-items-center justify-content-between mt-3 mb-1">
        <BackButton toFallback="/home" />
        <Breadcrumbs />
      </div>

      {initialLoading ? (
        <FormSkeleton />
      ) : (
        <>
          <div className="editprofile-title-minimal">
            <h2>EDITAR PERFIL</h2>
            <p className="editprofile-count">
              Atualize suas informações pessoais
            </p>
          </div>

          <div className="form-container">
            <form onSubmit={handleSubmit} className="profile-form">
              {isAdmin && (
                <>
                  <label htmlFor="type" className="form-label">
                    Tipo
                  </label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="formsInside"
                    style={{ height: "auto" }}
                  >
                    <option value="">Selecione um tipo</option>
                    <option value="Ex-Aluno">Ex-Aluno</option>
                    <option value="Homenageado">Homenageado</option>
                    <option value="Morador">Morador</option>
                    <option value="Bixo">Bixo</option>
                  </select>
                </>
              )}

              <label htmlFor="name" className="form-label">
                Nome
              </label>
              <input
                id="name"
                type="text"
                name="name"
                placeholder="Nome"
                value={formData.name}
                onChange={handleChange}
                required
              />

              <label htmlFor="nickname" className="form-label">
                Apelido
              </label>
              <input
                id="nickname"
                type="text"
                name="nickname"
                placeholder="Apelido"
                value={formData.nickname}
                onChange={handleChange}
              />

              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                id="email"
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
                required
              />

              <label htmlFor="telephone" className="form-label">
                Telefone
              </label>
              <PhoneInput
                id="telephone"
                country={"br"}
                value={formData.telephone}
                onChange={(phone) =>
                  setFormData((prev) => ({ ...prev, telephone: phone }))
                }
                inputProps={{
                  name: "telephone",
                  required: true,
                  autoFocus: false,
                  placeholder: "Telefone",
                  className: "profile-form-input",
                }}
                enableSearch={true}
                buttonStyle={{ display: "none" }}
                inputStyle={{ flex: 1 }}
              />

              <label htmlFor="address" className="form-label">
                Endereço
              </label>
              <input
                id="address"
                type="text"
                name="address"
                placeholder="Endereço"
                value={formData.address}
                onChange={handleChange}
              />

              <label htmlFor="city" className="form-label">
                Cidade
              </label>
              <input
                id="city"
                type="text"
                name="city"
                placeholder="Cidade"
                value={formData.city}
                onChange={handleChange}
              />

              <label htmlFor="state" className="form-label">
                Estado
              </label>
              <select
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="formsInside"
                style={{ height: "auto" }}
              >
                <option value="">Selecione um estado</option>
                {brazilianStates.map((state, index) => (
                  <option key={index} value={state}>
                    {state}
                  </option>
                ))}
              </select>

              <label htmlFor="zipcode" className="form-label">
                CEP
              </label>
              <InputMask
                id="zipcode"
                mask="99999-999"
                name="zipcode"
                placeholder="CEP"
                value={formData.zipcode}
                onChange={handleChange}
              >
                {(inputProps) => <input {...inputProps} />}
              </InputMask>

              <label htmlFor="birthday" className="form-label">
                Data de Nascimento
              </label>
              <input
                id="birthday"
                type="date"
                name="birthday"
                value={formData.birthday?.substring(0, 10)}
                onChange={handleChange}
              />

              <label htmlFor="graduation" className="form-label">
                Data do Quadrinho
              </label>
              <input
                id="graduation"
                type="date"
                name="graduation"
                value={formData.graduation?.substring(0, 10)}
                onChange={handleChange}
              />

              <label htmlFor="course" className="form-label">
                Curso
              </label>
              <input
                id="course"
                type="text"
                name="course"
                placeholder="Curso"
                value={formData.course}
                onChange={handleChange}
              />

              <label htmlFor="hometown" className="form-label">
                Cidade Natal
              </label>
              <input
                id="hometown"
                type="text"
                name="hometown"
                placeholder="Cidade Natal"
                value={formData.hometown}
                onChange={handleChange}
              />

              <label htmlFor="instagram" className="form-label">
                Link do Instagram
              </label>
              <input
                id="instagram"
                type="link"
                name="instagram"
                placeholder="Link do Instagram"
                value={formData.instagram}
                onChange={handleChange}
              />

              <label htmlFor="facebook" className="form-label">
                Link do Facebook
              </label>
              <input
                id="facebook"
                type="link"
                name="facebook"
                placeholder="Link do Facebook"
                value={formData.facebook}
                onChange={handleChange}
              />

              <label htmlFor="linkedin" className="form-label">
                Link do LinkedIn
              </label>
              <input
                id="linkedin"
                type="link"
                name="linkedin"
                placeholder="Link do LinkedIn"
                value={formData.linkedin}
                onChange={handleChange}
              />

              {/* Campo Foto do Quadrinho */}
              <label className="form-label">Foto do Quadrinho</label>
              <div className="image-upload-wrapper">
                <label
                  htmlFor="photoGraduation"
                  className="custom-image-upload"
                >
                  {previewGraduation ? (
                    <div className="image-preview-container">
                      <img
                        src={previewGraduation}
                        alt="Preview Foto do Quadrinho"
                        className="image-preview"
                      />
                      <div className="image-action-buttons">
                        <button
                          type="button"
                          className="edit-image-btn"
                          title="Substituir imagem"
                          onClick={(e) => {
                            e.preventDefault();
                            document.getElementById("photoGraduation").click();
                          }}
                        >
                          <span role="img" aria-label="Editar">
                            ✏️
                          </span>
                        </button>
                        <button
                          type="button"
                          className="remove-image-btn"
                          title="Remover imagem"
                          onClick={(e) => {
                            e.preventDefault();
                            handleOpenDeleteModal("graduation");
                          }}
                        >
                          <span role="img" aria-label="Excluir">
                            🗑️
                          </span>
                        </button>
                      </div>
                      <input
                        id="photoGraduation"
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoGraduationChange}
                        style={{ display: "none" }}
                      />
                    </div>
                  ) : (
                    <span className="upload-placeholder">
                      Selecionar imagem
                    </span>
                  )}
                  <input
                    id="photoGraduation"
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoGraduationChange}
                    style={{ display: "none" }}
                  />
                </label>
              </div>

              {/* Campo Foto Atual */}
              <label className="form-label">Foto Atual</label>
              <div className="image-upload-wrapper">
                <label htmlFor="photoNew" className="custom-image-upload">
                  {previewNew ? (
                    <div className="image-preview-container">
                      <img
                        src={previewNew}
                        alt="Preview Foto do Atual"
                        className="image-preview"
                      />
                      <div className="image-action-buttons">
                        <button
                          type="button"
                          className="edit-image-btn"
                          title="Substituir imagem"
                          onClick={(e) => {
                            e.preventDefault();
                            document.getElementById("photoNew").click();
                          }}
                        >
                          <span role="img" aria-label="Editar">
                            ✏️
                          </span>
                        </button>
                        <button
                          type="button"
                          className="remove-image-btn"
                          title="Remover imagem"
                          onClick={(e) => {
                            e.preventDefault();
                            handleOpenDeleteModal("new");
                          }}
                        >
                          <span role="img" aria-label="Excluir">
                            🗑️
                          </span>
                        </button>
                      </div>
                      <input
                        id="photoNew"
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoNewChange}
                        style={{ display: "none" }}
                      />
                    </div>
                  ) : (
                    <span className="upload-placeholder">
                      Selecionar imagem
                    </span>
                  )}
                  <input
                    id="photoNew"
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoNewChange}
                    style={{ display: "none" }}
                  />
                </label>
              </div>

              <div className="form-buttons">
                <button
                  className="btn btn-primary rounded-pill w-100"
                  type="submit"
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm"></span>
                  ) : (
                    "Salvar"
                  )}
                </button>
              </div>
            </form>{" "}
          </div>
          {/* O modal deve ficar FORA da form-container para centralizar corretamente */}
        </>
      )}
      {showDeleteModal && (
        <ModalPortal>
          <div
            className={`editprofile-delete-modal ${isModalClosing ? 'closing' : ''}`}
            tabIndex="-1"
            onClick={handleCloseDeleteModal}
          >
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirmar exclusão</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseDeleteModal}
                    aria-label="Fechar modal"
                  ></button>
                </div>
                <div className="modal-body">
                  Tem certeza que deseja excluir esta imagem?
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseDeleteModal}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleConfirmDelete}
                  >
                    Excluir
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show"></div>
          </div>
        </ModalPortal>
      )}
    </>
  );
}
